import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isLandingOnly } from '../utils/buildMode';
import { Button } from './shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './shadcn/dropdown-menu';
import { LogOut, Camera, User, Menu, X, BarChart3, Upload, FileText, Tag, Mail } from 'lucide-react';

function Navbar() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  async function handleLogout() {
    try {
      logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out');
    }
  }

  return (
    <header className="border-b bg-white shadow-sm sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          <div className="flex items-center">
            <Link to="/" className="flex items-center font-bold">
              <img src="/logo.png" alt="NeuralVision Logo" className="h-10" />
            </Link>

            {/* Desktop Navigation */}
            <nav className="ml-8 hidden md:flex items-center space-x-6">
              <Link
                to="/about"
                className="text-sm font-medium transition-colors hover:text-blue-600 flex items-center"
              >
                <FileText className="h-4 w-4 mr-1" />
                About Us
              </Link>
              {!isLandingOnly && (
                <Link
                  to="/contact-us"
                  className="text-sm font-medium transition-colors hover:text-blue-600 flex items-center"
                >
                  <Mail className="h-4 w-4 mr-1" />
                  Contact Us
                </Link>
              )}
              <Link
                to="/hardware"
                className="text-sm font-medium transition-colors hover:text-blue-600 flex items-center"
              >
                <Camera className="h-4 w-4 mr-1" />
                Our Hardware
              </Link>
              <Link
                to="/pricing"
                className="text-sm font-medium transition-colors hover:text-blue-600 flex items-center"
              >
                <Tag className="h-4 w-4 mr-1" />
                Pricing
              </Link>
              {currentUser && (
                <>
                  <Link 
                    to="/dashboard" 
                    className="text-sm font-medium transition-colors hover:text-blue-600 flex items-center"
                  >
                    <BarChart3 className="h-4 w-4 mr-1" />
                    Dashboard
                  </Link>
                  <Link 
                    to="/upload" 
                    className="text-sm font-medium transition-colors hover:text-blue-600 flex items-center"
                  >
                    <Upload className="h-4 w-4 mr-1" />
                    Upload
                  </Link>
                </>
              )}
            </nav>
          </div>

          <div className="flex items-center">
            {currentUser ? (
              <div className="flex items-center">
                {/* Mobile menu button */}
                <Button 
                  variant="ghost" 
                  className="md:hidden mr-2 p-2"
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                >
                  {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </Button>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="relative h-10 w-10 rounded-full border-2 border-blue-500 hover:bg-blue-50 transition-colors">
                      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-full flex items-center justify-center">
                        <User className="h-5 w-5" />
                      </div>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-64 p-4 bg-white rounded-lg shadow-xl border border-gray-200" align="end">
                    <div className="flex items-center space-x-3 mb-3 pb-3 border-b border-gray-100">
                      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-full flex items-center justify-center w-10 h-10">
                        <User className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{currentUser.companyName}</p>
                        <p className="text-xs text-gray-500 truncate max-w-[120px]">{currentUser.email}</p>
                      </div>
                    </div>
                    <DropdownMenuItem onClick={handleLogout} className="cursor-pointer py-2 px-3 rounded-md hover:bg-gray-100 transition-colors">
                      <LogOut className="mr-2 h-4 w-4 text-gray-500" />
                      <span>Log out</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : isLandingOnly ? (
              <div className="flex items-center space-x-2">
                <Link to="/contact-us">
                  <Button className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white">
                    <Mail className="mr-2 h-4 w-4" />
                    Contact Us
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Link to="/login">
                  <Button variant="ghost">Login</Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white">Register</Button>
                </Link>
              </div>
            )}
          </div>
        </div>
        
        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t">
            <nav className="flex flex-col space-y-3">
              <Link
                to="/about"
                className="text-sm font-medium transition-colors hover:text-blue-600 flex items-center py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                <FileText className="h-4 w-4 mr-2" />
                About Us
              </Link>
              {!isLandingOnly && (
                <Link
                  to="/contact-us"
                  className="text-sm font-medium transition-colors hover:text-blue-600 flex items-center py-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Contact Us
                </Link>
              )}
              <Link
                to="/hardware"
                className="text-sm font-medium transition-colors hover:text-blue-600 flex items-center py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                <Camera className="h-4 w-4 mr-2" />
                Our Hardware
              </Link>
              <Link
                to="/pricing"
                className="text-sm font-medium transition-colors hover:text-blue-600 flex items-center py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                <Tag className="h-4 w-4 mr-2" />
                Pricing
              </Link>
              {currentUser && (
                <>
                  <Link 
                    to="/dashboard" 
                    className="text-sm font-medium transition-colors hover:text-blue-600 flex items-center py-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <BarChart3 className="h-4 w-4 mr-2" />
                    Dashboard
                  </Link>
                  <Link 
                    to="/upload" 
                    className="text-sm font-medium transition-colors hover:text-blue-600 flex items-center py-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload
                  </Link>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}

export default Navbar;